import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCColorPicker, RCCGrid, RCCHookToast, RCCInput, RCCRangeSlider, RCCRefModalProps, RCCServicesApi, RCCTextArea} from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Audit from "src/components/Audit";
import ModalSide from "src/components/Modal/styles";
import returnColor from "src/utils/returnColor";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import addAndChangeSchemaPerspective from "./schemas";
import { IMetba002, IModalPerspective, IRefPerspectiveModals } from "./interface";

const ModalsPerspective: React.ForwardRefRenderFunction<IRefPerspectiveModals, IModalPerspective> = ({
	map,
	perspectives,
	setPerspectives,
	onCallbackMap
},ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { handleSubmit : handleSubmitAddOrChange, register : registerAddOrChange, control: controlAddOrChange, reset : resetAddOrChange, setError : setErrorAddOrChange, formState: { errors : errorsAddOrChange }, clearErrors: clearErrorsAddOrChange} = useForm();
	const [perspectiveSelected, setPerspectiveSelected] = useState<IMetba002>({} as IMetba002);

	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);

	const { setLoading } = useLoaderBackdrop();

	const modalAddOrChange = useRef<RCCRefModalProps>(null);

	const openModal = (perspective?: IMetba002) => {
		if(perspective){
			actionButtonChange(perspective);
			return;
		}

		actionButtonAdd();
	};

	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setIsAdd(true);
		setPerspectiveSelected({} as IMetba002);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (perspective: IMetba002) => {
		resetAddOrChange(); 
		setIsAdd(false);
		queryPerspective(perspective.mea002Id);
	};

	const submitAddOrChange = async (data: any) => {
		try{
			const dataForm:IMetba002 ={
				...data,
				mea002Id: Number(data.mea002Id),
				mea002Mea001Id: map.mea001Id,
				mea002Peso: data.mea002Peso ? data.mea002Peso : 0,
				mea002Cor: data.mea002Cor ? data.mea002Cor : "#F5AB35",
				mea001Nome: map.mea001Nome
			};

			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			await addAndChangeSchemaPerspective(translate).validate({...dataForm}, {
				abortEarly: false
			});

			if(isAdd){
				const result = await RCCServicesApi.postRequest({
					method: "Perspective/Insert",
					body: {
						...dataForm,
						mea002Id: 0		
					}
				});

				setPerspectives([...perspectives, {
					...data,
					id: String(result.data),
					mea002Id: result.data,
					mea002Mea001Id: map.mea001Id
				}]);

			}else{
				await RCCServicesApi.putRequest({
					method: "Perspective/Update",
					body: {
						...dataForm
					}
				});

				setPerspectives((prevState) => {
					const updateState = prevState.map((perspective) => {
						if(perspective.mea002Id === Number(data.mea002Id)){
							return {
								...perspective,
								mea002Nome: data.mea002Nome,
								mea002Cor: data.mea002Cor,
								mea002Peso: data.mea002Peso
							};
						}
						return perspective;
					});
					return updateState;
				});
			}

			if(onCallbackMap)
				    onCallbackMap();

			addToast({
				color: "green",
				description: isAdd ? translate("Perspectiva adicionada com sucesso") : translate("Perspectiva alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
				
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar perspectiva") : translate("Erro ao alterar perspectiva"), addToast);
		}
	};

	const queryPerspective = async (id: number) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Perspective/Select",
				urlParameters: {
					id
				}
			});
			setPerspectiveSelected(data);
			modalAddOrChange.current?.openModal();
			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModal
	}));

	return (
		<>
			{/* Modal Add an Change */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Perspectiva") : translate("Alterar Perspectiva"),
					subtitle: !isAdd ? `${translate("Perspectiva")}: ${perspectiveSelected?.mea002Nome}` : "",
					icon: {
						name: "mapSigns"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						alignItems="center"
						justifyContent="flex-end"
					>
						{
							perspectiveSelected && <Audit data={perspectiveSelected} type="pop"/>
						}
					</RCCGrid>
					<RCCGrid styleInline="display: none">
						<RCCInput 
							register={registerAddOrChange}
							name="mea002Id"
							label=""
							defaultValue={perspectiveSelected?.mea002Id}
							readOnly
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
					>
						<RCCInput 
							register={registerAddOrChange}
							name="mea002Nome"
							label={translate("Nome")}
							maxLength={100}
							defaultValue={perspectiveSelected?.mea002Nome}
							error={errorsAddOrChange.mea002Nome}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
					>
						<RCCTextArea 
							register={registerAddOrChange}
							name="mea002Descricao"
							label={translate("Descrição")}
							maxLength={2000}
							defaultValue={perspectiveSelected?.mea002Descricao}
							error={errorsAddOrChange.mea002Descricao}
							required
						/>
					</RCCGrid>
					<RCCGrid 
						container
						item
						xs={12}	
					>
						<RCCRangeSlider
							control={controlAddOrChange}
							name="mea002Peso"
							color="purple"
							label={translate("Peso")}
							defaultValue={!isAdd ? perspectiveSelected?.mea002Peso : 1}
							marks={[
								{
									value: 0,
									label: "0"
								},
								{
									value: 100,
									label: "100"
								}
							]}
							min={0}
							valueLabelDisplay="on"
							max={100}
							styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						alignItems="flex-end"
						styleInline="height: 50px"
					>
						<RCCColorPicker
							name="mea002Cor"
							control={controlAddOrChange}
							label={translate("Cor")}
							labelPosition="start"
							colorPickerProps={{color:returnColor(perspectiveSelected?.mea002Cor || "#F5AB35")}}
							defaultValue={perspectiveSelected?.mea002Cor || "#F5AB35"}
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalsPerspective);
